import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { groupPositions, ProductPricePosition } from '../lib/pricePosition';
import type { ProductSummary } from './useProviderProducts';

/**
 * Where the supplier's own offers stand for the products on screen: lowest or not, per size.
 *
 * Only for a supplier looking at its own catalogue — pass a null providerId otherwise. The RPC
 * refuses anyone who is not an active member of the supplier, and a refusal just means no
 * badges: the catalogue itself must keep working.
 */
export const useProviderPricePositions = (providerId: string | null, products: ProductSummary[]) => {
  const [positions, setPositions] = useState<Record<string, ProductPricePosition>>({});

  // Keyed on the array itself, not its ids: a reload after a price edit returns the same
  // products with possibly a different standing.
  useEffect(() => {
    if (!providerId || products.length === 0) {
      setPositions({});
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_provider_price_positions', {
          p_provider_id: providerId,
          p_product_ids: products.map((p) => p.product_id),
        });
        if (cancelled) return;
        setPositions(error ? {} : groupPositions(data));
      } catch {
        if (!cancelled) setPositions({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [providerId, products]);

  return positions;
};
